import {
  CATCH_BOARD_STAGES,
  isCatchBoardClosedStage,
  isCatchBoardStage,
} from 'src/screens/catch-board/catchBoardConstants'
import { rebuildCatchBoardBoardState } from 'src/utils/catchBoardMappers'

export function findCatchBoardDealContainer(columns = {}, dealId) {
  const id = String(dealId || '')
  if (!id) {
    return null
  }

  for (const stage of CATCH_BOARD_STAGES) {
    const deals = columns[stage]?.deals || []
    if (deals.some(deal => String(deal.dealId) === id)) {
      return stage
    }
  }

  return null
}

export function resolveCatchBoardDropContainer(columns = {}, overId) {
  const id = String(overId || '')
  if (!id) {
    return null
  }

  if (isCatchBoardStage(id)) {
    return id
  }

  return findCatchBoardDealContainer(columns, id)
}

/**
 * Dragging onto Landed / Got Away from another stage is blocked —
 * those transitions go through the deal modal (won/lost fields).
 */
export function isCatchBoardClosedDropBlocked(fromStage, toStage) {
  if (!fromStage || !toStage || fromStage === toStage) {
    return false
  }
  return isCatchBoardClosedStage(toStage)
}

/**
 * Move a deal into a stage at index and renumber positions.
 * Returns a new board state (does not mutate).
 */
export function applyCatchBoardDealMove(
  board,
  { dealId, toStage, toIndex },
) {
  const fromStage = findCatchBoardDealContainer(board?.columns, dealId)
  if (!fromStage || !isCatchBoardStage(toStage)) {
    return board
  }

  const working = CATCH_BOARD_STAGES.reduce((acc, stage) => {
    acc[stage] = [...(board.columns?.[stage]?.deals || [])]
    return acc
  }, {})

  const fromList = working[fromStage]
  const fromIndex = fromList.findIndex(deal => String(deal.dealId) === String(dealId))
  if (fromIndex < 0) {
    return board
  }

  const [moved] = fromList.splice(fromIndex, 1)
  const targetList = working[toStage]
  let insertAt = Number(toIndex)

  if (!Number.isFinite(insertAt) || insertAt < 0) {
    insertAt = targetList.length
  }
  if (fromStage === toStage && fromIndex < insertAt) {
    insertAt -= 1
  }
  insertAt = Math.max(0, Math.min(insertAt, targetList.length))

  targetList.splice(insertAt, 0, {
    ...moved,
    stage: toStage,
  })

  const deals = CATCH_BOARD_STAGES.flatMap(stage =>
    working[stage].map((deal, position) => ({
      ...deal,
      stage,
      position,
    })),
  )

  return rebuildCatchBoardBoardState(deals, board.filters || {}, {
    momentum: board.totals?.momentum ?? null,
  })
}

/**
 * Resolve drop index within the target stage from an over id.
 */
export function resolveCatchBoardDropIndex(columns = {}, toStage, overId) {
  const deals = columns[toStage]?.deals || []
  const id = String(overId || '')

  if (!id || isCatchBoardStage(id)) {
    return deals.length
  }

  const index = deals.findIndex(deal => String(deal.dealId) === id)
  return index >= 0 ? index : deals.length
}
